import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import {
  ReportItem,
  TrialBalanceReportItem,
} from "types/Models/consolidatedReport";
import { VIEW_BY_OPTIONS } from "constants/reports";
import { PERIOD } from "constants/revenueMetrics";
import { FormikForm } from "components/FormikForm/FormikForm";
import { getUsedGroupBysForReport } from "utils/allReports";

type EditTrialBalanceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  report: TrialBalanceReportItem | null;
  onSave: (data: { groupBy: string; groupByLabel: string }) => void;
  modalTitle: string;
  submitText: string;
  isEditMode: boolean;
  entityId: string;
  selectedReports?: ReportItem[];
};

type FormValues = {
  selectedValue: string;
  selectedLabel: string;
};

export const TrialBalanceModal = ({
  isOpen,
  onClose,
  report,
  onSave,
  modalTitle,
  submitText,
  isEditMode,
  entityId,
  selectedReports = [],
}: EditTrialBalanceModalProps) => {
  const usedGroupBys = report
    ? getUsedGroupBysForReport(report.value, selectedReports)
    : [];

  const toOption = (label: string, value: string): OptionData => ({
    label,
    value,
    disableOption: usedGroupBys.includes(value),
    data: usedGroupBys.includes(value) ? "Added already" : undefined,
  });

  const noneOption = toOption("None", "");

  const periodGroup = [
    {
      label: "Period",
      options: VIEW_BY_OPTIONS.filter(
        ({ value }) => value === PERIOD.MONTHLY
      ).map(({ label, value }) => toOption(label, value)),
    },
  ];

  const groupByOptions = [noneOption, ...periodGroup];

  const allFlatOptions = [noneOption, ...periodGroup.flatMap((g) => g.options)];

  const periodValues = VIEW_BY_OPTIONS.filter(
    ({ value }) => value === PERIOD.MONTHLY
  ).map(({ value }) => value);

  const nextAvailableValue =
    periodValues.find((value) => !usedGroupBys.includes(value)) || "";

  const initialSelectedValue =
    report && isEditMode && report.groupBy === ""
      ? ""
      : report?.groupBy || nextAvailableValue;

  const initialSelectedLabel =
    (allFlatOptions.find((opt) => opt.value === initialSelectedValue)
      ?.label as string) ||
    report?.groupByLabel ||
    "";

  const initialValues: FormValues = {
    selectedValue: initialSelectedValue,
    selectedLabel: initialSelectedLabel,
  };

  const handleSubmit = (values: FormValues) => {
    const isPeriodGrouping = VIEW_BY_OPTIONS.some(
      (option) => option.value === values.selectedValue
    );

    const saveData = {
      groupBy: isPeriodGrouping ? values.selectedValue : "",
      groupByLabel: values.selectedLabel,
    };

    onSave(saveData);
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="regular" useCustomOverlay asChild>
        <FormikForm initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => {
            const selectedOption = allFlatOptions.find(
              (opt) => opt.value === values.selectedValue
            ) ?? { label: "None", value: "" };

            return (
              <>
                <Modal.Header>
                  <Modal.Title>{modalTitle}</Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body>
                  <div className="t-flex t-flex-col t-gap-4">
                    <div>
                      <Combobox
                        name="selectedValue"
                        label="Group by"
                        placeholder="None"
                        options={groupByOptions}
                        value={selectedOption}
                        onChange={(selected) => {
                          if (selected && "value" in selected) {
                            setFieldValue("selectedValue", selected.value);
                            setFieldValue(
                              "selectedLabel",
                              String(selected.label || "")
                            );
                          }
                        }}
                        block
                        required
                        withForm
                        menuPortalTarget={document.body}
                        isClearable={false}
                        tooltip
                        isOptionDisabled={(option) =>
                          Boolean(option.disableOption)
                        }
                      />
                    </div>
                  </div>
                </Modal.Body>

                <Modal.FooterButtonGroup>
                  <Button customType="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button customType="primary" type="submit">
                    {submitText}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
